<template>
  <v-container class="modern-master-view pb-12">
    <!-- Premium Global Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-exchange-alt</v-icon>
        </v-avatar>
        <div>
          <div class="d-flex align-center">
            <div class="text-h4 font-weight-black primary--text lh-1">Notas de Venta</div>
            <v-img src="../../assets/integrations/logo-softnet.png" max-width="100" class="ml-4 opacity-80" contain></v-img>
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Sincronización e integración con Softnet ERP</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <export-documents :filter="filter" :options="options" :disabled="loading"/>
      </div>
    </v-row>

    <!-- Metrics Cards Section (KPIs) -->
    <v-row class="mb-8 px-4">
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-primary shadow-sm">
          <v-avatar color="primary lighten-5" size="48" class="mr-4">
            <v-icon color="primary">fas fa-file-invoice</v-icon>
          </v-avatar>
          <div>
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Notas de Venta Sincronizadas</div>
            <div class="text-h5 font-weight-black primary--text">{{ totalDtes }} documentos</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-success shadow-sm">
          <v-avatar color="success lighten-5" size="48" class="mr-4">
            <v-icon color="success">fas fa-link</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Estatus Integración</div>
            <div class="d-flex align-center justify-space-between">
              <div class="text-h5 font-weight-black success--text">Softnet ERP Activo</div>
              <v-chip x-small color="success lighten-4" class="success--text font-weight-black px-2">
                Conectado
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Emitida Desde</div>
          <v-text-field 
            v-model='filter.created_after' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Emitida Hasta</div>
          <v-text-field 
            v-model='filter.created_before' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Vendedor</div>
          <v-select
            v-model="filter.seller"
            :items="sellers"
            item-text="email"
            item-value="id"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="auto" class="d-flex align-end pb-1 ml-auto">
          <v-btn color="primary" icon class="grey lighten-4" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="computedHeaders"
        :items="dtes"
        :options.sync="options"
        :server-items-length="totalDtes"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Slot -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Registros Sincronizados: {{totalDtes}}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por folio, cliente o RUT..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </div>
        </template>

        <!-- Cell Templates -->
        <template v-slot:item.created_at="{ item }">
          <span class="body-2 font-weight-medium grey--text text--darken-3">{{ item.created_at | moment('DD/MM/YYYY') }}</span>
        </template>

        <template v-slot:item.document="{ item }">
          <div class="d-block py-2">
            <softnet-detail :item="item">
              <span class="body-2 font-weight-black primary--text tracking-tighter cursor-pointer">#{{ item.document }}</span>
            </softnet-detail>
          </div>
        </template>

        <template v-slot:item.workorder="{ item }">
          <v-chip v-if="item.workorder" x-small color="primary lighten-5" class="primary--text font-weight-black border-primary-light" label outlined>
            OT #{{ item.workorder }}
          </v-chip>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.sales_note="{ item }">
          <span v-if="item.sales_note" class="caption grey--text font-weight-bold">CN #{{ item.sales_note }}</span>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.customer_name="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-bold grey--text text--darken-4 lh-1-2 mb-1">{{ item.customer_name }}</span>
            <span class="caption grey--text font-weight-black opacity-60 tracking-wider">{{ item.customer_rut }}</span>
          </div>
        </template>

        <template v-slot:item.neto="{ item }">
          <span class="body-1 font-weight-black primary--text tracking-tighter">{{ item.neto | currency('$', 0) }}</span>
        </template>

        <template v-slot:item.created_by_name="{ item }">
          <div class="d-flex align-center justify-end">
            <v-avatar size="24" color="grey lighten-4" class="mr-2 border-white-sm shadow-xs">
              <v-icon x-small color="grey darken-1">fas fa-user-tag</v-icon>
            </v-avatar>
            <span class="caption grey--text font-weight-bold">{{ item.created_by_name }}</span>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { fetchDocuments } from '@/api/softnet'
import { fetchSellers } from '@/api/auth'
import notifications from '@/utils/notifications'
import ExportDocuments from '@/components/softnet/ExportDocuments'
import SoftnetDetail from '@/components/softnet/SoftnetDetail.vue'
import moment from 'moment'

export default {
  name: 'Documents',
  components: { SoftnetDetail, ExportDocuments },
  data() {
    return {
      loading: false,
      dtes: [],
      sellers: [],
      timer: null,
      options: {},
      totalDtes: 0,
      search: '',
      filter: {
        created_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before: '',
        tipo: 30,
        seller: '',
      },
      headers: [
        { text: 'Fecha Sinc.', align: 'start', value: 'created_at', class: 'table-header', width: '130px' },
        { text: 'Folio Externo', align: 'start', value: 'document', class: 'table-header', width: '140px' },
        { text: 'OT', align: 'start', value: 'workorder', class: 'table-header', width: '110px' },
        { text: 'Cotización', align: 'start', value: 'sales_note', class: 'table-header', width: '110px' },
        { text: 'Cliente', align: 'start', value: 'customer_name', class: 'table-header' },
        { text: 'Monto Neto', value: 'neto', class: 'table-header', align: 'end', width: '140px' },
        { text: 'Registrado por', value: 'created_by_name', class: 'table-header', align: 'end', width: '180px' },
      ],
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = (sortBy && sortBy.length) ? sortBy[0] : '-created_at'
      
      const query = `ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&page=${page || 1}&page_size=${itemsPerPage || 20}&search=${this.search}&created_after=${this.filter.created_after || ''}&created_before=${this.filter.created_before || ''}&workorder__sales_note__customer__seller=${this.filter.seller || ''}`
      
      try {
        const res = await fetchDocuments(query)
        this.totalDtes = res.count
        this.dtes = res.results
      } catch (err) {
        notifications.showError('Error al sincronizar con Softnet')
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {  
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 800)
    },
  },
  async mounted() {
    this.fetch()
    this.sellers = await fetchSellers()
  },
  computed: {
    computedHeaders() { return this.headers }
  }
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; font-family: 'Outfit', sans-serif !important; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.border-left-success { border-left: 4px solid var(--v-success-base) !important; }
.border-primary-light { border-color: rgba(var(--v-primary-base), 0.1) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-wider { letter-spacing: 0.5px; }
.tracking-tighter { letter-spacing: -0.5px; }
.h-44 { height: 44px !important; }
.opacity-60 { opacity: 0.6; }
.opacity-80 { opacity: 0.8; }
.cursor-pointer { cursor: pointer; }

.modern-input >>> fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field { width: 320px; }
.modern-search-field >>> fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; }
</style>